import { TrendingUp, TrendingDown, Trophy, Target, Skull } from "lucide-react";
import { Modal, Feed, FeedItem, Button, Pill } from "@/components/ui";
import { fmtInt, fmtSigned, fmtGameMonth } from "@/lib/format";
import { cn } from "@/lib/cn";
import type { TurnResultOut } from "@/services/types";

type TurnSummaryModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  result: TurnResultOut | null;
};

export function TurnSummaryModal({ open, onOpenChange, result }: TurnSummaryModalProps) {
  if (!result) return null;

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      size="lg"
      title={`Souhrn měsíce ${result.month}`}
      description={fmtGameMonth(result.month)}
    >
      {/* Terminal state banners */}
      {result.bankrupted && (
        <Banner
          tone="loss"
          icon={<Skull size={18} />}
          title="Bankrot"
          description="Tvůj kapitál klesl pod hranici −500 000 Kč. Hra skončila."
        />
      )}
      {result.scenario_completed && (
        <Banner
          tone="amber"
          icon={<Trophy size={18} />}
          title={`Scénář splněn · ${result.scenario_stars} ★`}
          description="Gratulace! Cíl byl dosažen."
        />
      )}

      {/* KPI deltas */}
      <div className="grid grid-cols-3 gap-3 mt-4">
        <SummaryStat
          label="Čisté jmění"
          before={result.net_worth_before}
          after={result.net_worth_after}
        />
        <SummaryStat
          label="Hotovost"
          before={result.cash_before}
          after={result.cash_after}
        />
        <div className="rounded-xl border border-stroke bg-bg-raised p-3">
          <div className="text-[10px] uppercase tracking-[0.16em] text-ink-dim font-semibold">
            Cashflow
          </div>
          <div
            className={cn(
              "mono mt-1 text-base font-semibold",
              result.cashflow >= 0 ? "text-profit" : "text-loss",
            )}
          >
            {fmtSigned(result.cashflow)} Kč
          </div>
          <div className="mono mt-0.5 text-[10px] text-ink-dim">
            ▲ {fmtInt(result.total_income)} · ▼ {fmtInt(result.total_expenses)}
          </div>
        </div>
      </div>

      {/* (Kept intentionally empty — NW delta already shown inside the
          SummaryStat "Čisté jmění" card above. Removing the duplicate
          line that confused novice users who read it as "current NW = 0".) */}

      {/* Achievements + challenges */}
      {(result.achievements_unlocked.length > 0 ||
        result.challenges_completed.length > 0) && (
        <div className="mt-4 flex flex-wrap gap-2">
          {result.achievements_unlocked.map((a) => (
            <Pill key={a} tone="amber">
              <Trophy size={10} /> {a}
            </Pill>
          ))}
          {result.challenges_completed.map((c) => (
            <Pill key={c} tone="ok">
              <Target size={10} /> {c}
            </Pill>
          ))}
        </div>
      )}

      {/* Events */}
      {result.events.length > 0 && (
        <div className="mt-5">
          <div className="mb-2 text-xs font-semibold uppercase tracking-[0.18em] text-ink-dim">
            Události ({result.events.length})
          </div>
          <Feed>
            {result.events.slice(0, 8).map((ev, i) => (
              <FeedItem
                key={i}
                icon={
                  ev.cash_delta >= 0 ? (
                    <TrendingUp size={14} className="text-profit" />
                  ) : (
                    <TrendingDown size={14} className="text-loss" />
                  )
                }
                iconBg={ev.cash_delta >= 0 ? "bg-profit/10" : "bg-loss/10"}
                title={ev.title}
                meta={ev.description}
                amount={
                  ev.cash_delta !== 0 ? (
                    <span
                      className={ev.cash_delta >= 0 ? "text-profit" : "text-loss"}
                    >
                      {fmtSigned(ev.cash_delta)}
                    </span>
                  ) : null
                }
              />
            ))}
          </Feed>
        </div>
      )}

      <div className="mt-6 flex justify-end">
        <Button variant="primary" onClick={() => onOpenChange(false)}>
          Pokračovat
        </Button>
      </div>
    </Modal>
  );
}

function SummaryStat({
  label,
  before,
  after,
}: {
  label: string;
  before: number;
  after: number;
}) {
  const delta = after - before;
  return (
    <div className="rounded-xl border border-stroke bg-bg-raised p-3">
      <div className="text-[10px] uppercase tracking-[0.16em] text-ink-dim font-semibold">
        {label}
      </div>
      <div className="mono mt-1 text-base font-semibold">{fmtInt(after)} Kč</div>
      <div
        className={cn(
          "mono mt-0.5 text-[10px]",
          delta >= 0 ? "text-profit" : "text-loss",
        )}
      >
        {fmtSigned(delta)} Kč m/m
      </div>
    </div>
  );
}

function Banner({
  tone,
  icon,
  title,
  description,
}: {
  tone: "loss" | "amber" | "ok";
  icon: React.ReactNode;
  title: string;
  description: string;
}) {
  const bg = {
    loss: "border-loss/40 bg-loss/10 text-loss",
    amber: "border-amber/40 bg-amber-dim text-amber-bright",
    ok: "border-profit/40 bg-profit/10 text-profit",
  }[tone];
  return (
    <div className={cn("rounded-xl border px-4 py-3 flex items-start gap-3", bg)}>
      <div className="mt-0.5">{icon}</div>
      <div>
        <div className="font-serif font-semibold">{title}</div>
        <div className="mt-0.5 text-sm text-ink-muted">{description}</div>
      </div>
    </div>
  );
}
